/**
 * Boundary decoders for the untyped JSON the knowledge-base pipeline emitted.
 *
 * `JSON.parse` hands back `any`; these predicates and readers are the single
 * place a runtime `typeof` is allowed (they narrow into a real type), so every
 * caller branches on a domain value instead of widening an unparsed one. The
 * `unknown` parameters are the JSON-walker / type-predicate seams documented in
 * `oxlint.config.ts`.
 */

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate at the JSON I/O boundary
function isString(value: unknown): value is string {
  return typeof value === "string";
}

// oxlint-disable-next-line anti-slop/no-unknown-parameters -- type predicate at the JSON I/O boundary
function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Read a string field, falling back when absent or the wrong type. */
// oxlint-disable-next-line anti-slop/no-unknown-parameters -- JSON walker at the KB file boundary
export function asString(value: unknown, fallback: string): string {
  return isString(value) ? value : fallback;
}

/** Read an optional non-blank string; `null` when absent, blank or wrong type. */
// oxlint-disable-next-line anti-slop/no-unknown-parameters -- JSON walker at the KB file boundary
export function asOptionalString(value: unknown): string | null {
  return isString(value) && value.trim().length > 0 ? value.trim() : null;
}

/** Read a finite number field, falling back when absent or the wrong type. */
// oxlint-disable-next-line anti-slop/no-unknown-parameters -- JSON walker at the KB file boundary
export function asNumber(value: unknown, fallback: number): number {
  return isFiniteNumber(value) ? value : fallback;
}

/** Read an array of non-blank strings, dropping anything that is not one. */
// oxlint-disable-next-line anti-slop/no-unknown-parameters -- JSON walker at the KB file boundary
export function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const out: string[] = [];
  for (const item of value) {
    if (isString(item) && item.trim().length > 0) {
      out.push(item.trim());
    }
  }
  return out;
}
